import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ChevronDown, Loader2 } from 'lucide-react';
import matchIcon from '../assets/icons/homepage/match_icon.svg';
import { cn } from '@/lib/utils';
import { useRTL, useTranslation } from '../utils/translations';
import { useGetConfiguration } from '@adloid-technologies/discover-sdk';
import { usePackageStore } from '@adloid-technologies/discover-sdk/dist/esm/store/package-store';
import {
  getInventoryRecommendations,
  getAutoIntelRPOCode,
  dispatchAutoIntelEvent,
  AutoIntelEvents,
} from '../utils/autointel';
import { InventoryRecommendation } from '../utils/autointel/autointel.types';
import { uimetadata, metadata, assetSrc, assetUrl } from '../utils/metadata';
import { MatchDifferencesPanel } from './MatchDifferencesPanel';
import { DEFAULT_REGIONS, DEALER_AUTONTEL_ID_MAP, type Dealer } from './DealerSelectorDialog';
import { useLogEvent, AnalyticsIds, useAnalyticsSegmentationContext } from '../utils/useAnalytics';

// Tahoe (2365) left-panel sidebar renders, per trim and per exterior colour:
// public/assets/images/2365/local_matches/<TRIM folder>/<file>.png. Folders follow
// the variant's `trim`; files are keyed by the colour's English label (the source
// files spell Radiant as "Radient"). TAHOE_LM_AVAILABLE lists the renders that exist
// - a trim/colour pair without one falls back to the colour's own image instead of
// a broken thumbnail.
const TAHOE_LM_FOLDER: Record<string, string> = {
  LS: 'LS', LT: 'LT', RST: 'RST', Z71: 'Z71', Premier: 'PREMIERE',
};
const TAHOE_LM_FILE: Record<string, string> = {
  'Cypress Grey': 'TexA_Cypress_Grey',
  'Summit White': 'TexB_Summit_White',
  Black: 'TexC_Black',
  'Lakeshore Blue Metallic': 'TexD_Lakeshore_Blue_Metallic',
  'Dark Ash Metallic': 'TexE_Dark_Ash_Metallic',
  'Sterling Gray Metallic': 'TexF_Sterling_Gray_Metallic',
  'Radiant Red Tintcoat': 'TexG_Radient_Red_Tintcoat',
  'Iridescent Pearl Tintcoat': 'TexH_Iridescent_Pearl_Tintcoat',
  'Polar White Tricoat': 'TexI_Polar_White_Tricoat',
};
const TAHOE_LM_AVAILABLE = new Set([
  'LS/TexB_Summit_White',
  'LS/TexC_Black',
  'LS/TexD_Lakeshore_Blue_Metallic',
  'LS/TexE_Dark_Ash_Metallic',
  'LS/TexF_Sterling_Gray_Metallic',
  'LS/TexG_Radient_Red_Tintcoat',
  'LT/TexB_Summit_White',
  'LT/TexC_Black',
  'LT/TexD_Lakeshore_Blue_Metallic',
  'LT/TexE_Dark_Ash_Metallic',
  'LT/TexF_Sterling_Gray_Metallic',
  'LT/TexG_Radient_Red_Tintcoat',
  'PREMIERE/TexC_Black',
  'PREMIERE/TexD_Lakeshore_Blue_Metallic',
  'PREMIERE/TexE_Dark_Ash_Metallic',
  'PREMIERE/TexF_Sterling_Gray_Metallic',
  'PREMIERE/TexG_Radient_Red_Tintcoat',
  'PREMIERE/TexH_Iridescent_Pearl_Tintcoat',
  'PREMIERE/TexI_Polar_White_Tricoat',
  'RST/TexA_Cypress_Grey',
  'RST/TexB_Summit_White',
  'RST/TexC_Black',
  'RST/TexE_Dark_Ash_Metallic',
  'RST/TexF_Sterling_Gray_Metallic',
  'RST/TexG_Radient_Red_Tintcoat',
  'RST/TexH_Iridescent_Pearl_Tintcoat',
  'Z71/TexA_Cypress_Grey',
  'Z71/TexB_Summit_White',
  'Z71/TexC_Black',
  'Z71/TexE_Dark_Ash_Metallic',
  'Z71/TexF_Sterling_Gray_Metallic',
  'Z71/TexG_Radient_Red_Tintcoat',
  'Z71/TexH_Iridescent_Pearl_Tintcoat',
]);

// Corvette left-panel sidebar images (public/assets/images/2387/s44/), keyed by
// exterior color id. Matched to each color by visually comparing the rendered car
// against the color's real-world swatch (two reds exist in the s44 set — the
// brighter one is Torch Red, the deeper metallic one is Crimson Velocity Red).
const CORVETTE_S44_IMAGE: Record<string, string> = {
  extArcticWhite: 'images/2387/s44/Corvette 8_white.png',
  extBlack: 'images/2387/s44/Corvette 3_black.png',
  extTorchRed: 'images/2387/s44/Corvette 4_red.png',
  extCompetitionYellow: 'images/2387/s44/Corvette 5_yellow.png',
  extSebringOrange: 'images/2387/s44/Corvette 6_orange.png',
  extRoswellGreen: 'images/2387/s44/Corvette 7_green.png',
  extBladeSilver: 'images/2387/s44/Corvette 2_silver.png',
  extCrimsonVelocityRed: 'images/2387/s44/Corvette 9_red.png',
};

interface LocalMatchesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  totalPrice?: number;
  selectedDealer: string | null;
  onDealerChange: (dealerId: string) => void;
  onProceed?: () => void;
}

export const LocalMatchesDialog: React.FC<LocalMatchesDialogProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  selectedDealer,
  onProceed,
  onDealerChange,
}) => {
  const { variantID, exteriorColorID, interiorColorID, accessoriesIDs, packageIDs: initialPackageIDs } =
    useGetConfiguration();
  const { packageIDs: runtimePackageIDs } = usePackageStore();
  // Merge initial (URL-param) packages with runtime-selected packages — stable reference
  const packageIDs = useMemo(
    () => [...new Set([...(initialPackageIDs ?? []), ...(runtimePackageIDs ?? [])])],
    [initialPackageIDs, runtimePackageIDs],
  );
  const { isRTL } = useRTL();
  const { t } = useTranslation();
  const { trackEvent } = useLogEvent();
  const { setSticky } = useAnalyticsSegmentationContext();

  /** Returns the translated region heading, e.g. "وكلاء في الإمارات" in Arabic */
  const getRegionHeading = (region: typeof DEFAULT_REGIONS[0]) =>
    region.cityKey
      ? t(
          'staticTranslations.selectDealership.dealersInCity',
          region.region,
          { city: t(`staticTranslations.countryNames.${region.cityKey}`, region.cityKey) },
        )
      : region.region;

  /** Returns the translated availability text for a dealer card */
  const getDealerAvailability = (dealer: Dealer) =>
    dealer.availabilityTranslationKey
      ? t(
          dealer.availabilityTranslationKey,
          dealer.availability,
          dealer.availabilityParams
            ? Object.fromEntries(
                Object.entries(dealer.availabilityParams).map(([k, v]) => [
                  k,
                  k === 'city' ? t(`staticTranslations.countryNames.${v}`, v) : v,
                ])
              )
            : undefined,
        )
      : dealer.availability;
  const modelId = uimetadata.config.model;
  const modelData = (uimetadata.models as any)[modelId];
  const rawTransitionImage = (uimetadata as any).config?.presentation?.transitionImage as string | undefined;
  const brandLogoSrc: string | null = rawTransitionImage
    ? `${assetUrl}${rawTransitionImage}`
    : (modelData?.logo ?? null);

  const [recommendations, setRecommendations] = useState<InventoryRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<InventoryRecommendation | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDealerDropdownOpen, setIsDealerDropdownOpen] = useState(false);
  const [isSpecsOpen, setIsSpecsOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const dealerDropdownRef = useRef<HTMLDivElement>(null);

  // Derive active dealer from shared state
  const allDealers: Dealer[] = DEFAULT_REGIONS.flatMap(r => r.dealers);
  const activeDealer = useMemo(
    () => allDealers.find(d => d.id === selectedDealer) ?? null,
    [allDealers, selectedDealer],
  );
  // AutoIntel dealer id used to filter inventory (e.g. 'al-gandhi' -> 'aga')
  const autointelDealerId = useMemo(
    () => (selectedDealer ? DEALER_AUTONTEL_ID_MAP[selectedDealer] ?? null : null),
    [selectedDealer],
  );
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dealerDropdownRef.current && !dealerDropdownRef.current.contains(e.target as Node)) {
        setIsDealerDropdownOpen(false);
      }
    };
    if (isDealerDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDealerDropdownOpen]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Build RPO feature codes from the current SDK configuration
  const buildFeatureCodes = useCallback((): string[] => {
    const codes: string[] = [];
    const graphicsModel = (metadata as any)?.models?.[modelId];
    if (variantID) {
      const variantRPO = getAutoIntelRPOCode(variantID);
      if (variantRPO) codes.push(variantRPO);
    }
    const extRPO = getAutoIntelRPOCode(exteriorColorID);
    if (extRPO) codes.push(extRPO);
    // Interior colour RPO depends on the model + seat trim — pass variantID to resolve it.
    const intRPO = getAutoIntelRPOCode(interiorColorID, variantID);
    if (intRPO) codes.push(intRPO);
    (accessoriesIDs ?? []).forEach((id: string) => {
      const graphicsAcc = graphicsModel?.accessories?.[id];
      if (graphicsAcc?.display === false) return;
      const rpo = getAutoIntelRPOCode(id);
      if (rpo) codes.push(rpo);
    });
    (packageIDs ?? []).forEach((id: string) => {
      const rpo = getAutoIntelRPOCode(id);
      if (rpo) codes.push(rpo);
    });
    return [...new Set(codes)];
  }, [variantID, exteriorColorID, interiorColorID, accessoriesIDs, packageIDs, modelId]);

  // Fetch recommendations when dialog opens or selected dealer changes
  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    const features = buildFeatureCodes();
    const dealerIds = autointelDealerId ? [autointelDealerId] : undefined;
    getInventoryRecommendations(modelId, features, 9, dealerIds)
      .then(setRecommendations)
      .catch(() => setRecommendations([]))
      .finally(() => setIsLoading(false));
  }, [isOpen, modelId, buildFeatureCodes, autointelDealerId]);

  if (!isOpen) return null;

  // — Derived configured vehicle data —
  const variantLabel = isRTL
    ? (modelData?.variants?.[variantID]?.name?.ar ?? modelData?.variants?.[variantID]?.name?.en ?? variantID)
    : (modelData?.variants?.[variantID]?.name?.en ?? variantID);
  const extColorLabel = isRTL
    ? (modelData?.colors?.exterior?.[exteriorColorID]?.label?.ar ?? modelData?.colors?.exterior?.[exteriorColorID]?.label?.en ?? 'Standard')
    : (modelData?.colors?.exterior?.[exteriorColorID]?.label?.en ?? 'Standard');
  const intColorLabel = isRTL
    ? (modelData?.colors?.interior?.[interiorColorID]?.label?.ar ?? modelData?.colors?.interior?.[interiorColorID]?.label?.en ?? 'Standard')
    : (modelData?.colors?.interior?.[interiorColorID]?.label?.en ?? 'Standard');
  const extColorImage = modelData?.colors?.exterior?.[exteriorColorID]?.image;
  const sidebarImage = extColorImage ? assetSrc(extColorImage) : '';
  const modelNameFull = (uimetadata.config as any).modelName || modelId;

  // Local matches sidebar image — from public/assets/images/2378/local_matches/<variant folder>/<color label>.png
  const variantTrim = (modelData?.variants?.[variantID] as any)?.trim as string | undefined;
  const localMatchesFolder = variantTrim === 'Z71' ? 'Z71' : 'LT';
  const extColorLabelEn = (modelData?.colors?.exterior?.[exteriorColorID] as any)?.label?.en as string | undefined;
  // Resolve actual filename — some files have known typos vs metadata labels
  const localMatchesFilename = (() => {
    if (!extColorLabelEn) return null;
    if (extColorLabelEn === 'Radiant Red Tincoat') return 'Radiant Red Tintcoat';
    if (extColorLabelEn === 'Lakeshore Blue Metallic' && localMatchesFolder === 'Z71') return 'vLakeshore Blue Metallic';
    return extColorLabelEn;
  })();
  // The local_matches render sheet is Traverse (2378) specific. Tahoe (2365) has its own
  // per-trim renders and Corvette uses the s44 per-color renders; other projects fall
  // back to the exterior color image (or a hex swatch below).
  const tahoeLmPath = (() => {
    if (modelId !== 'Tahoe' || !extColorLabelEn) return null;
    const folder = variantTrim ? TAHOE_LM_FOLDER[variantTrim] : undefined;
    const file = TAHOE_LM_FILE[extColorLabelEn];
    const key = folder && file ? `${folder}/${file}` : '';
    return key && TAHOE_LM_AVAILABLE.has(key) ? `images/2365/local_matches/${key}.png` : null;
  })();
  const localMatchesSidebarImage = (modelId === 'Traverse' && localMatchesFilename)
    ? assetSrc(`images/2378/local_matches/${localMatchesFolder}/${localMatchesFilename}.png`)
    : tahoeLmPath
    ? assetSrc(tahoeLmPath)
    : modelId === 'Corvette' && CORVETTE_S44_IMAGE[exteriorColorID]
    ? assetSrc(CORVETTE_S44_IMAGE[exteriorColorID])
    : sidebarImage;
  const extColorHex = (modelData?.colors?.exterior?.[exteriorColorID] as { hex?: string } | undefined)?.hex;

  // Sidebar specs derived from uimetadata + current config
  const sidebarSpecs = [
    { label: t('staticTranslations.localMatches.specModel', 'Model'), value: modelNameFull },
    { label: t('staticTranslations.localMatches.specTrim', 'Trim'), value: variantLabel },
    { label: t('staticTranslations.localMatches.specExteriorColor', 'Exterior Color'), value: extColorLabel },
    { label: t('staticTranslations.localMatches.specInteriorColor', 'Interior Color'), value: intColorLabel },
    ...((modelData?.specifications?.summary ?? []) as any[])
      .filter((s: any) =>
        ['Transmission', 'Seating Capacity', 'Wheelbase', 'Infotainment Screen'].includes(
          s.title?.en ?? ''
        )
      )
      .slice(0, 4)
      .map((s: any) => ({
        label: isRTL ? (s.title?.ar ?? s.title?.en ?? '') : (s.title?.en ?? ''),
        value: isRTL ? (s.subtitle?.ar ?? s.subtitle?.en ?? '') : (s.subtitle?.en ?? ''),
      })),
  ];

  // Helper: get match percentage — score is already 0-100
  const getMatchPercent = (rec: InventoryRecommendation): number => {
    const s = typeof rec.score === 'number' ? rec.score : 0;
    return Math.round(s);
  };

  // Helper: format price — uses currency from API response, falls back to AED
  const formatAEDPrice = (rec: InventoryRecommendation): string | null => {
    const raw = rec.price?.offer_gross ?? rec.price?.base_gross ?? rec.price?.total_gross ?? null;
    if (!raw) return null;
    const currency = rec.price?.currency || 'AED';
    return `${currency} ${Number(raw).toLocaleString()}`;
  };

  // Helper: get best image URL from recommendation
  const getMatchImage = (rec: InventoryRecommendation): string => {
    const imgs = rec.images ?? [];
    const first = imgs[0] ?? '';
    if (first.startsWith('http') || first.startsWith('/') || first.startsWith('blob')) return first;
    return sidebarImage || 'https://placehold.co/291x146/eaeaea/aaaaaa?text=Vehicle';
  };

  // Filter recommendations by selected dealer (name-based match), sorted by score descending
  const filteredRecommendations = (autointelDealerId
    ? recommendations.filter(rec => (rec.dealer?.id ?? '').toLowerCase() === autointelDealerId.toLowerCase())
    : recommendations
  ).slice().sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  const handleClose = () => {
    trackEvent(AnalyticsIds.inventoryClose, {
      parent: 'inventory',
      primaryTypeName: String(filteredRecommendations.length),
    });
    onClose();
  };

  const handleCloseDifferences = () => {
    trackEvent(AnalyticsIds.viewDifferencesInteract, {
      state: false,
      primaryTypeName: selectedMatch?.trim?.name || '',
    });
    setSelectedMatch(null);
  };

  // — Theme colors —
  const bg = isDarkMode ? 'bg-[#111111]' : 'bg-white';
  const sidebarBg = isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white';
  const cardBg = isDarkMode ? 'bg-[#242424]' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-black';
  const textMuted = isDarkMode ? 'text-white/50' : 'text-[#777]';
  const textLabel = isDarkMode ? 'text-white/80' : 'text-[#282828]';
  const borderColor = isDarkMode ? 'border-white/10' : 'border-[#e0e0e0]';

  // — Card component —
  const MatchCard: React.FC<{ rec: InventoryRecommendation; position: number }> = ({ rec, position }) => {
    const matchPct = getMatchPercent(rec);
    const isExact = matchPct === 100;
    const matchBadgeLabel = isExact
      ? t('staticTranslations.localMatches.exactMatch', 'Exact Match')
      : t('staticTranslations.localMatches.similarMatch', 'Close Match');
    const badgeBg = isExact
      ? isDarkMode ? 'bg-[#1e4620]' : 'bg-[#d4edda]'
      : isDarkMode ? 'bg-[#4a3e10]' : 'bg-[#f9eac1]';
    const modelYear = rec.model?.year ?? '';
    const modelName = rec.model?.name ?? modelNameFull;
    const trimName = rec.trim?.name ?? variantLabel;
    const img = getMatchImage(rec);
    const priceLabel = formatAEDPrice(rec);

    return (
      <div
        className={cn(
          'w-full h-[396px] relative overflow-hidden rounded-[var(--brand-card-radius)] border-[1.2px]',
          isDarkMode ? 'bg-[#242424] border-white/10' : 'bg-white border-[rgba(230,230,230,0.93)]'
        )}
      >
        {/* Top section: badge + available now + model name | view differences */}
        <div
          className={cn('absolute flex gap-[33px] items-start', isRTL ? 'right-[14px] left-[14px]' : 'left-[14px] right-[14px]')}
          style={{ top: '28.8px' }}
        >
          {/* Left column */}
          <div className="flex flex-col gap-[9.5px] items-start flex-1 min-w-0">
            <div className="flex flex-col gap-[6.654px] items-start">
              <div className={cn('flex items-center justify-center px-[6.726px] py-[6.726px] rounded-[var(--brand-card-radius-sm)]', badgeBg)}>
                <span className={cn('text-[14px] capitalize font-semibold', isDarkMode ? 'text-white' : 'text-black')}>
                  {matchBadgeLabel}
                </span>
              </div>
            </div>
            <p className={cn('text-[16px] font-bold leading-snug', textPrimary)}>
              {modelYear} {modelName}
              <br />
              {trimName}
            </p>
          </div>

          {/* View differences link */}
          <button
            onClick={() => {
              trackEvent(AnalyticsIds.viewDifferencesInteract, {
                state: true,
                primaryTypeName: trimName,
                secondaryTypeName: String(matchPct),
                tertiaryTypeName: String(position),
              });
              setSelectedMatch(rec);
            }}
            className="text-[14px] font-medium capitalize text-[var(--brand-accent)] hover:underline whitespace-nowrap flex-shrink-0"
          >
            {t('staticTranslations.localMatches.viewDifferences', 'view differences')}
          </button>
        </div>

        {/* Vehicle Image — centered, vertically offset per Figma */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ top: 'calc(50% + 23px)', width: '286px', height: '143.5px' }}
        >
          <img
            src={img}
            alt={`${modelName} ${trimName}`}
            className="w-full h-full object-contain"
            referrerPolicy="no-referrer"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = 'https://placehold.co/286x143/eaeaea/aaaaaa?text=Vehicle';
            }}
          />
        </div>

        {/* Match % gradient pill */}
        <div
          className={cn('absolute', isRTL ? 'right-[27.8px]' : 'left-[27.8px]')}
          style={{
            top: '298px',
            display: 'inline-flex',
            padding: '5px 14px 5px 12px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '7px',
            borderRadius: 'var(--brand-pill-radius)',
            border: '0.5px solid #F26621',
            background: 'linear-gradient(90deg, #F2BD17 -58.55%, rgba(242, 189, 23, 0.00) 79.61%)',
          }}
        >
          <img src={matchIcon} alt="" aria-hidden="true" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <span style={{ color: 'var(--brand-accent-secondary)', fontFamily: "var(--default-font-family)", fontSize: '14px', fontStyle: 'normal', fontWeight: 600, lineHeight: 'normal', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {matchPct}% {t('staticTranslations.localMatches.match', 'Match')}
          </span>
        </div>

        {/* AED price — bottom */}
        {priceLabel && (
          <p
            className={cn('absolute text-[14px] font-bold leading-none', isDarkMode ? 'text-white' : 'text-black', isRTL ? 'left-[14px]' : 'right-[14px]')}
            style={{ top: '309px' }}
          >
            {priceLabel}
          </p>
        )}

        {/* Proceed button */}
        <div
          className="absolute left-[24px] right-[24px]"
          style={{ top: '336.8px', height: '34.219px' }}
        >
          <button
            onClick={() => {
              trackEvent(AnalyticsIds.inventoryProceedInteract, {
                parent: 'inventory',
                primaryTypeName: trimName,
                secondaryTypeName: String(matchPct),
                tertiaryTypeName: priceLabel || '',
                quaternaryTypeName: String(position),
              });
              dispatchAutoIntelEvent(AutoIntelEvents.CONVERSION_GET_QUOTE, rec.vin);
              onProceed?.();
            }}
            className="w-full h-full bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white text-[15.208px] font-medium capitalize rounded-[var(--brand-button-radius-sm)] transition-colors"
          >
            {t('staticTranslations.localMatches.getQuote', 'Proceed')}
          </button>
        </div>
      </div>
    );
  };

  // ————————— DESKTOP LAYOUT —————————
  if (!isMobile) {
    return (
      <div className={cn('fixed inset-0 z-[10000] flex', bg)} dir={isRTL ? 'rtl' : 'ltr'} style={{ fontFamily: 'inherit' }}>
        {/* Left Sidebar */}
        <div
          className={cn(
            'w-[406px] h-full flex-shrink-0 flex flex-col overflow-y-auto border-r',
            sidebarBg,
            borderColor
          )}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className={cn(
              'absolute top-5 z-10 w-7 h-7 rounded-full border flex items-center justify-center',
              isRTL ? 'left-6' : 'right-6',
              isDarkMode
                ? 'bg-[#333] border-white/10 text-white/60 hover:text-white'
                : 'bg-white border-[#d0d0d0] text-[#666] hover:text-black',
            )}
          >
            <X size={14} strokeWidth={2} />
          </button>

          {/* Brand logo + What you configured + Model name */}
          <div className="pt-[15px] px-[38px] flex flex-col gap-[25px]">
            {brandLogoSrc && (
              <img src={brandLogoSrc} alt="Brand Logo" className="h-[43px] w-[101px] object-contain object-left" />
            )}
            <div className="flex flex-col gap-[8px]">
              <p className={cn('text-[16px] font-bold tracking-[0.35px]', isDarkMode ? 'text-white/80' : 'text-[#333334]')}>
                {t('staticTranslations.localMatches.whatYouConfigured', 'What you configured')}
              </p>
              <p className={cn('text-[32px] font-bold leading-tight whitespace-pre-line break-words uppercase', textPrimary)}>
                {modelNameFull}
                {'\n'}
                {variantLabel}
              </p>
            </div>
          </div>

          {/* Vehicle image */}
          <div className="px-[38px] mt-[20px] flex items-center justify-center h-[161px]">
            {(localMatchesSidebarImage || sidebarImage) ? (
              <img
                src={localMatchesSidebarImage || sidebarImage}
                alt={modelNameFull}
                className="w-[319px] h-[161px] object-contain"
                referrerPolicy="no-referrer"
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  if (e.currentTarget.src !== sidebarImage && sidebarImage) {
                    e.currentTarget.src = sidebarImage;
                  }
                }}
              />
            ) : (
              <div
                className={cn('w-[319px] h-[161px] flex items-center justify-center rounded', !extColorHex && (isDarkMode ? 'bg-white/5' : 'bg-gray-100'))}
                style={extColorHex ? { backgroundColor: extColorHex } : undefined}
              >
                {!extColorHex && <span className={cn('text-[11px]', textMuted)}>{modelNameFull}</span>}
              </div>
            )}
          </div>

          {/* Specs list */}
          <div className="mt-8 px-[38px] pb-10 flex flex-col gap-[18px]">
            {sidebarSpecs.map((spec, i) => (
              <div key={i} className="flex gap-[4px] items-start">
                <span className={cn('text-[16px] font-medium leading-normal whitespace-nowrap', isDarkMode ? 'text-white/80' : 'text-[#282828]')}>{spec.label}:</span>
                <span className={cn('text-[16px] font-medium leading-normal', isDarkMode ? 'text-white/50' : 'text-[#777]')}>{spec.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 h-full flex flex-col min-h-0">
          {/* Content header: Matching vehicle label + Dealer section */}
          <div className={cn('flex-shrink-0 flex items-start justify-between pt-[47px] pb-[16px]', isRTL ? 'pr-[30px] pl-[80px]' : 'pl-[30px] pr-[80px]')} ref={dealerDropdownRef}>
            {/* Matching vehicle available in stock */}
            <p className={cn('text-[16px] font-bold tracking-[0.35px] pt-[12px]', isDarkMode ? 'text-white/90' : 'text-[#333334]')}>
              {t('staticTranslations.localMatches.matchingInStock', 'Matching vehicle available in stock')}
            </p>
            <div className={cn('flex flex-col gap-[4px]', isRTL ? 'items-start' : 'items-end')}>
            <p className={cn('text-[16px] font-semibold', textPrimary)}>
              {activeDealer ? activeDealer.name : (DEFAULT_REGIONS[0] ? getRegionHeading(DEFAULT_REGIONS[0]) : t('staticTranslations.selectDealership.title', 'Select Dealer'))}
            </p>
            <div className="relative">
              <button
                onClick={() => setIsDealerDropdownOpen(prev => !prev)}
                className="text-[var(--brand-accent)] text-[14px] font-semibold inline-flex items-center gap-[5px] hover:underline"
              >
                {t('staticTranslations.localMatches.changeDealer', 'Change Dealer')}
                <ChevronDown
                  size={12}
                  className={cn('transition-transform duration-200', isDealerDropdownOpen && 'rotate-180')}
                />
              </button>

              {/* Dealer dropdown panel */}
              {isDealerDropdownOpen && (
                <div
                  className={cn(
                    'absolute top-7 z-50 rounded-[var(--brand-card-radius-lg)] border shadow-xl p-3 flex flex-col gap-3 w-[280px] max-h-[60vh] overflow-y-auto',
                    isRTL ? 'left-0' : 'right-0',
                    isDarkMode ? 'bg-[#1e1e1e] border-white/10' : 'bg-white border-[#e0e0e0]'
                  )}
                  style={isRTL ? { paddingLeft: '30px' } : { paddingRight: '30px' }}
                >
                  {DEFAULT_REGIONS.map(region => (
                    <div key={region.region}>
                      <p className={cn('text-[10px] font-semibold uppercase tracking-wide mb-2 px-1', textMuted)}>
                        {getRegionHeading(region)}
                      </p>
                      <div className="flex flex-col gap-2">
                        {region.dealers.map(dealer => (
                          <button
                            key={dealer.id}
                            onClick={() => {
                              trackEvent(AnalyticsIds.changeDealerInteract, {
                                parent: 'inventory',
                                primaryTypeName: dealer.name,
                              });
                              setSticky({ dealerName: dealer.name });
                              onDealerChange(dealer.id);
                              setIsDealerDropdownOpen(false);
                            }}
                            className={cn(
                              'w-full px-3 py-2.5 rounded-[var(--brand-button-radius)] border transition-all',
                              isRTL ? 'text-right' : 'text-left',
                              selectedDealer === dealer.id
                                ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10'
                                : isDarkMode
                                ? 'border-white/10 hover:border-white/30 bg-white/5'
                                : 'border-[#e0e0e0] hover:border-[var(--brand-accent)] bg-[#fafafa]'
                            )}
                          >
                            <p className={cn('text-[12px] font-semibold', textPrimary)}>{dealer.name}</p>
                            <p className={cn('text-[10px] mt-0.5', textMuted)}>{getDealerAvailability(dealer)}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

          {/* Cards grid — only this scrolls */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center w-full h-full min-h-[300px]">
                <Loader2 className={cn('w-8 h-8 animate-spin', textMuted)} />
              </div>
            ) : filteredRecommendations.length === 0 ? (
              <div className="flex items-center justify-center w-full h-full min-h-[300px]">
                <p className={cn('text-[14px] font-medium', textMuted)}>
                  {t('staticTranslations.localMatches.noMatches', 'No matching vehicles found in your area.')}
                </p>
              </div>
            ) : (
              <div className="grid gap-[14px] pl-[16px] pr-[16px] pb-20 pt-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 300px))' }}>
                {filteredRecommendations.map((rec, idx) => <MatchCard key={rec.id || rec.vin} rec={rec} position={idx + 1} />)}
              </div>
            )}
          </div>
        </div>

        {/* Differences side panel */}
        <MatchDifferencesPanel
          isOpen={!!selectedMatch}
          onClose={handleCloseDifferences}
          isDarkMode={isDarkMode}
          match={selectedMatch ?? undefined}
          variantID={variantID}
          exteriorColorID={exteriorColorID}
          interiorColorID={interiorColorID}
          packageIDs={packageIDs ?? []}
          accessoriesIDs={accessoriesIDs ?? []}
        />
      </div>
    );
  }

  // ————————— MOBILE LAYOUT —————————
  // Derive the region label for the dealer section (e.g. "Dealers in Abu Dhabi")
  const activeDealerRegion = (() => {
    const region = selectedDealer
      ? DEFAULT_REGIONS.find(r => r.dealers.some(d => d.id === selectedDealer)) ?? DEFAULT_REGIONS[0]
      : DEFAULT_REGIONS[0];
    return region ? getRegionHeading(region) : 'your area';
  })();

  return (
    <div
      className={cn(
        'fixed inset-0 z-[10000] flex flex-col overflow-hidden',
        isDarkMode ? 'bg-[#111111]' : 'bg-white'
      )}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* ── Mobile Header: brand logo + close button ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-[23px] pt-[16px] pb-[8px]">
        {brandLogoSrc ? (
          <img
            src={brandLogoSrc}
            alt="Brand Logo"
            className="h-[28px] w-[63px] object-contain object-left"
          />
        ) : (
          <div className="h-[28px] w-[63px]" />
        )}
        <button
          onClick={handleClose}
          className={cn(
            'w-[22px] h-[22px] rounded-full border flex items-center justify-center flex-shrink-0',
            isDarkMode
              ? 'bg-[#333] border-white/20 text-white/60'
              : 'bg-white border-[#d0d0d0] text-[#666]'
          )}
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      </div>

      {/* —— What you configured + Model title —— */}
      <div className="flex-shrink-0 px-[28px] pb-[14px] flex flex-col gap-[4px]">
        <p className={cn('text-[13px] font-bold tracking-[0.35px]', isDarkMode ? 'text-white/60' : 'text-[#666]')}>
          {t('staticTranslations.localMatches.whatYouConfigured', 'What you configured')}
        </p>
        <p
          className={cn('text-[20px] font-bold leading-snug uppercase', isDarkMode ? 'text-white' : 'text-[#333334]')}
          style={{ fontFamily: "var(--default-font-family)" }}
        >
          {modelNameFull}
        </p>
      </div>

      {/* ── Vehicle image — not scrollable ── */}
      {(localMatchesSidebarImage || sidebarImage) && (
        <div className="flex-shrink-0 flex justify-center px-[25px] pb-[4px]">
          <img
            src={localMatchesSidebarImage || sidebarImage}
            alt={modelNameFull}
            className="w-[245px] h-[124px] object-contain"
            referrerPolicy="no-referrer"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              if (e.currentTarget.src !== sidebarImage && sidebarImage) {
                e.currentTarget.src = sidebarImage;
              }
            }}
          />
        </div>
      )}

      {/* ── View Details accordion — not scrollable ── */}
      <div className="flex-shrink-0">
        {/* Accordion header */}
        <button
          onClick={() => setIsSpecsOpen(prev => !prev)}
          className={cn(
            'w-full flex items-center justify-between px-[25px] py-[12px] border-t',
            isDarkMode ? 'border-white/10' : 'border-[#e3e3e3]'
          )}
        >
          <span
            className={cn('text-[16px] font-bold', isDarkMode ? 'text-white' : 'text-[#333334]')}
            style={{ fontFamily: "var(--default-font-family)" }}
          >
            {t('staticTranslations.localMatches.viewDetails', 'View Details')}
          </span>
          <ChevronDown
            size={16}
            className={cn(
              'transition-transform duration-200 flex-shrink-0',
              isDarkMode ? 'text-white' : 'text-[#333334]',
              isSpecsOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Accordion body — specs list */}
        {isSpecsOpen && (
          <div className="px-[25px] pb-[12px] flex flex-col gap-[9px]">
            {sidebarSpecs.map((spec, i) => (
              <p
                key={i}
                className="text-[12px] leading-snug"
                style={{ fontFamily: "var(--default-font-family)" }}
              >
                <span className={cn('font-medium', isDarkMode ? 'text-white/80' : 'text-[#282828]')}>
                  {spec.label}:{' '}
                </span>
                <span className={cn('font-normal', isDarkMode ? 'text-white/50' : 'text-[#777]')}>
                  {spec.value}
                </span>
              </p>
            ))}
          </div>
        )}
      </div>

      {/* ── Dealer header — not scrollable ── */}
      <div
        ref={dealerDropdownRef}
        className={cn('flex-shrink-0 relative flex items-center justify-between px-[25px] py-[12px]')}
      >
        <p
          className={cn('text-[16px] font-bold', isDarkMode ? 'text-white' : 'text-[#333334]')}
          style={{ fontFamily: "var(--default-font-family)" }}
        >
          {activeDealerRegion}
        </p>
        <button
          onClick={() => setIsDealerDropdownOpen(prev => !prev)}
          className="text-[var(--brand-accent)] text-[14px] font-bold inline-flex items-center gap-[4px]"
          style={{ fontFamily: "var(--default-font-family)" }}
        >
          {t('staticTranslations.localMatches.changeDealer', 'Change Dealer')}
          <ChevronDown
            size={14}
            className={cn('transition-transform duration-200', isDealerDropdownOpen && 'rotate-180')}
          />
        </button>

        {/* Mobile dealer dropdown panel */}
        {isDealerDropdownOpen && (
          <div
            className={cn(
              'absolute left-0 right-0 top-full z-50 rounded-[var(--brand-card-radius-lg)] border shadow-xl p-3 flex flex-col gap-3 mt-1 mx-[25px]',
              isDarkMode ? 'bg-[#1e1e1e] border-white/10' : 'bg-white border-[#e0e0e0]'
            )}
          >
            {DEFAULT_REGIONS.map(region => (
              <div key={region.region}>
                <p className={cn('text-[10px] font-semibold uppercase tracking-wide mb-2 px-1', textMuted)}>
                  {getRegionHeading(region)}
                </p>
                <div className="flex flex-col gap-2">
                  {region.dealers.map(dealer => (
                    <button
                      key={dealer.id}
                      onClick={() => {
                        trackEvent(AnalyticsIds.changeDealerInteract, {
                          parent: 'inventory',
                          primaryTypeName: dealer.name,
                        });
                        setSticky({ dealerName: dealer.name });
                        onDealerChange(dealer.id);
                        setIsDealerDropdownOpen(false);
                      }}
                      className={cn(
                        'w-full px-3 py-2.5 rounded-[var(--brand-button-radius)] border transition-all',
                        isRTL ? 'text-right' : 'text-left',
                        selectedDealer === dealer.id
                          ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10'
                          : isDarkMode
                          ? 'border-white/10 hover:border-white/30 bg-white/5'
                          : 'border-[#e0e0e0] hover:border-[var(--brand-accent)] bg-[#fafafa]'
                      )}
                    >
                      <p className={cn('text-[12px] font-semibold', textPrimary)}>{dealer.name}</p>
                      <p className={cn('text-[10px] mt-0.5', textMuted)}>{getDealerAvailability(dealer)}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* —— Matching vehicle heading —— */}
      <div className="flex-shrink-0 px-[25px] py-[10px]">
        <p className={cn('text-[14px] font-bold tracking-[0.3px]', isDarkMode ? 'text-white/90' : 'text-[#333334]')}>
          {t('staticTranslations.localMatches.matchingInStock', 'Matching vehicle available in stock')}
        </p>
      </div>
      {/* ── Cards — only this section scrolls ── */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-[19px] pb-10 pt-[8px]">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className={cn('w-8 h-8 animate-spin', textMuted)} />
          </div>
        ) : filteredRecommendations.length === 0 ? (
          <p className={cn('text-[13px] font-medium py-12 text-center', textMuted)}>
            {t('staticTranslations.localMatches.noMatches', 'No matching vehicles found in your area.')}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredRecommendations.map((rec, mobileIdx) => {
              const matchPct = getMatchPercent(rec);
              const isExact = matchPct === 100;
              const matchBadgeLabel = isExact
                ? t('staticTranslations.localMatches.exactMatch', 'Exact Match')
                : t('staticTranslations.localMatches.similarMatch', 'Similar Match');
              const badgeBg = isExact ? 'bg-[#d4edda]' : 'bg-[#f9eac1]';
              const badgeBgDark = isExact ? 'bg-[#1e4620]' : 'bg-[#4a3e10]';
              const img = getMatchImage(rec);
              const modelYear = rec.model?.year ?? '';
              const matchModelName = rec.model?.name ?? modelNameFull;
              const trimName = rec.trim?.name ?? variantLabel;
              const priceLabel = formatAEDPrice(rec);
              return (
                /* ── Figma card: border, rounded-[9px], h-[352px], overflow-hidden ── */
                <div
                  key={rec.id || rec.vin}
                  className={cn(
                    'relative w-full h-[352px] rounded-[var(--brand-card-radius)] overflow-hidden border',
                    isDarkMode ? 'bg-[#242424] border-white/10' : 'bg-white border-[#e3e3e3]'
                  )}
                >
                  {/* Match badge — top-start (RTL-aware) */}
                  <div
                    className={cn(
                      'absolute top-[15px] flex items-center justify-center px-[4px] py-[4px] rounded-[var(--brand-card-radius-sm)]',
                      isRTL ? 'right-[23px]' : 'left-[23px]',
                      isDarkMode ? badgeBgDark : badgeBg
                    )}
                  >
                    <span
                      className={cn(
                        'text-[12px] font-bold capitalize leading-none',
                        isDarkMode ? 'text-white' : 'text-black'
                      )}
                      style={{ fontFamily: "var(--default-font-family)" }}
                    >
                      {matchBadgeLabel}
                    </span>
                  </div>

                  {/* View Differences — top-end (RTL-aware) */}
                  <button
                    onClick={() => {
                      trackEvent(AnalyticsIds.viewDifferencesInteract, {
                        state: true,
                        primaryTypeName: trimName,
                        secondaryTypeName: String(matchPct),
                        tertiaryTypeName: String(mobileIdx + 1),
                      });
                      setSelectedMatch(rec);
                    }}
                    className={cn('absolute top-[19px] text-[12px] capitalize text-[#2c73d4] hover:underline leading-none', isRTL ? 'left-[23px]' : 'right-[23px]')}
                    style={{ fontFamily: "var(--default-font-family)" }}
                  >
                    {t('staticTranslations.localMatches.viewDifferences', 'View Differences')}
                  </button>

                  {/* Available Now */}
                  <p
                    className={cn(
                      'absolute top-[44px] text-[12px] uppercase leading-none',
                      isRTL ? 'right-[23px]' : 'left-[23px]',
                      isDarkMode ? 'text-white/70' : 'text-black'
                    )}
                    style={{ fontFamily: "var(--default-font-family)" }}
                  >
                    {t('staticTranslations.localMatches.availableNow', 'available now')}
                  </p>

                  {/* Model name — wraps to 2 lines */}
                  <p
                    className={cn(
                      'absolute top-[68px] text-[16px] font-bold leading-snug w-[135px]',
                      isRTL ? 'right-[23px]' : 'left-[23px]',
                      isDarkMode ? 'text-white' : 'text-[#333334]'
                    )}
                    style={{ fontFamily: "var(--default-font-family)" }}
                  >
                    {modelYear} {matchModelName}
                    <br />
                    {trimName}
                  </p>

                  {/* Vehicle image — centered per Figma (top: 50% + 16px offset) */}
                  <div
                    className="absolute"
                    style={{
                      left: '50%',
                      top: 'calc(50% + 16px)',
                      transform: 'translate(-50%, -50%)',
                      width: '291px',
                      height: '146px',
                    }}
                  >
                    <img
                      src={img}
                      alt={`${matchModelName} ${trimName}`}
                      className="w-full h-full object-contain"
                      referrerPolicy="no-referrer"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.src =
                          'https://placehold.co/291x146/eaeaea/aaaaaa?text=Vehicle';
                      }}
                    />
                  </div>

                  {/* Match % gradient pill */}
                  <div
                    className={cn('absolute', isRTL ? 'right-[23px]' : 'left-[23px]')}
                    style={{
                      top: '270px',
                      display: 'inline-flex',
                      padding: '5px 12px 5px 10px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '6px',
                      borderRadius: 'var(--brand-pill-radius)',
                      border: '0.5px solid #F26621',
                      background: 'linear-gradient(90deg, #F2BD17 -58.55%, rgba(242, 189, 23, 0.00) 79.61%)',
                    }}
                  >
                    <img src={matchIcon} alt="" aria-hidden="true" style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                    <span style={{ color: 'var(--brand-accent-secondary)', fontFamily: "var(--default-font-family)", fontSize: '12px', fontStyle: 'normal', fontWeight: 600, lineHeight: 'normal', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {matchPct}% {t('staticTranslations.localMatches.match', 'Match')}
                    </span>
                  </div>

                  {/* AED price — bottom right */}
                  {priceLabel && (
                    <p
                      className={cn('absolute text-[13px] font-bold leading-none', isDarkMode ? 'text-white' : 'text-black', isRTL ? 'left-[23px]' : 'right-[23px]')}
                      style={{ top: '276px', fontFamily: "var(--default-font-family)" }}
                    >
                      {priceLabel}
                    </p>
                  )}

                  {/* Proceed button — fixed at bottom per Figma (top: 301px) */}
                  <div
                    className="absolute"
                    style={{
                      left: '50%',
                      top: '301px',
                      transform: 'translateX(-50%)',
                      width: '293px',
                      height: '32px',
                    }}
                  >
                    <button
                      onClick={() => {
                        trackEvent(AnalyticsIds.inventoryProceedInteract, {
                          parent: 'inventory',
                          primaryTypeName: trimName,
                          secondaryTypeName: String(matchPct),
                          tertiaryTypeName: priceLabel || '',
                          quaternaryTypeName: String(mobileIdx + 1),
                        });
                        dispatchAutoIntelEvent(AutoIntelEvents.CONVERSION_GET_QUOTE, rec.vin);
                        onProceed?.();
                      }}
                      className="w-full h-full bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white text-[14px] font-bold capitalize rounded-[var(--brand-button-radius-sm)] transition-colors"
                      style={{ fontFamily: "var(--default-font-family)" }}
                    >
                      {t('staticTranslations.localMatches.getQuote', 'Proceed')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Differences side panel (mobile) */}
      <MatchDifferencesPanel
        isOpen={!!selectedMatch}
        onClose={handleCloseDifferences}
        isDarkMode={isDarkMode}
        match={selectedMatch ?? undefined}
        variantID={variantID}
        exteriorColorID={exteriorColorID}
        interiorColorID={interiorColorID}
        packageIDs={packageIDs ?? []}
        accessoriesIDs={accessoriesIDs ?? []}
      />
    </div>
  );
};
